import React, { useRef, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Chip,
  TextField,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as UserIcon,
  Visibility as ViewIcon,
  AccountCircle as AccountCircleIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getBookingStatusColor, renderThumbnail } from '../../../utils/helper';
import { fetchBookingFilters } from "../../../services/common";
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { useModal } from '../../../contexts/ModalContext';
import AddBookingFinance from '../AddBookingFinance';
import CreateNewBooking from '../CreateNewBooking';
import BookingNotes from '../BookingNotes';
import PhoneInput from '../../../components/common/PhoneInput';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';
import { bookingTokens } from './bookingDetailsTheme';

const defaultReferenceData = {
  agents: [],
  projects: [],
  leads: [],
  bookingStatusOptions: [],
  paymentStatusOptions: [],
  paymentModeOptions: [],
  units: {},
  filtered_projects: [],
  other_charges: []
};

export default function BookingSummaryCard({
  booking,
  onStatusUpdate,
  onActionComplete,
  fetchBookingDetails,
  editFinanceOpen: controlledEditFinanceOpen,
  onEditFinanceOpen,
  onEditFinanceClose,
  compactLayout = false,
}) {
  const {
    booking_id,
    booking_date,
    status,
    status_verbose,
    payment_breakdown,
    customer,
    sale_order,
    agent,
    property,
    project,
    unit,
    lead
  } = booking;

  const bookingIdRef = useRef();
  const statusTriggerRef = useRef();
  const bookingDateTriggerRef = useRef(null);
  const tokenDateTriggerRef = useRef(null);

  const { updateBookingStatus, updateBookingDetails, updatePrimaryCustomer, updateBookingDates } = useBookingDetails();
  const { openSuccessModal, openErrorModal, openConfirmModal } = useModal();

  const [internalEditFinanceOpen, setInternalEditFinanceOpen] = useState(false);
  const isControlledFinance = controlledEditFinanceOpen !== undefined;
  const editFinanceDialogOpen = isControlledFinance ? controlledEditFinanceOpen : internalEditFinanceOpen;
  const closeEditFinance = () => {
    if (onEditFinanceClose) onEditFinanceClose();
    else setInternalEditFinanceOpen(false);
  };
  const openEditFinance = () => {
    if (onEditFinanceOpen) onEditFinanceOpen();
    else setInternalEditFinanceOpen(true);
  };

  const [editBookingDialogOpen, setEditBookingDialogOpen] = useState(false);
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [new_status, setNewStatus] = useState(null);
  const [bookingStatuses, setBookingStatuses] = useState([]);
  const [referenceData, setReferenceData] = useState(defaultReferenceData || {});
  const [editingPrimaryCustomer, setEditingPrimaryCustomer] = useState(false);
  const [primaryCustomerForm, setPrimaryCustomerForm] = useState({ customer_name: '', customer_email: '', customer_phone: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [editingBookingDate, setEditingBookingDate] = useState(false);
  const [editingTokenDate, setEditingTokenDate] = useState(false);

  // ✅ FIX: Initialize date states directly from booking props so DatePicker
  // always has a value to highlight when first opened.
  const [bookingDate, setBookingDate] = useState(booking_date ? dayjs(booking_date) : null);
  const [tokenDate, setTokenDate] = useState(booking?.sale_order?.token_paid_on ? dayjs(booking.sale_order.token_paid_on) : null);

  // ✅ FIX: Keep date states in sync whenever booking prop changes (e.g. after refetch)
  useEffect(() => {
    setBookingDate(booking_date ? dayjs(booking_date) : null);
  }, [booking_date]);

  useEffect(() => {
    setTokenDate(booking?.sale_order?.token_paid_on ? dayjs(booking.sale_order.token_paid_on) : null);
  }, [booking?.sale_order?.token_paid_on]);

  // Compact layout: popover picker open state (no inline edit view)
  const [bookingDatePickerOpen, setBookingDatePickerOpen] = useState(false);
  const [tokenDatePickerOpen, setTokenDatePickerOpen] = useState(false);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);

  // Copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(booking_id);
    setCopyTooltipOpen(true);
    setTimeout(() => setCopyTooltipOpen(false), 2000);
  };

  const handleEditBookingClose = () => {
    setEditBookingDialogOpen(false);
  };

  const handleSaveFinance = async (updatedFinanceData) => {
    try {
      closeEditFinance();
      if (new_status === 'pending') {
        openSuccessModal('Success', 'Booking submitted for approval successfully!');
      } else {
        openSuccessModal('Success', 'Finance details updated successfully!');
      }
      if (fetchBookingDetails) {
        await fetchBookingDetails(booking.id);
      }
    } catch (error) {
      console.error('Error updating finance:', error);
      openErrorModal('Error', 'Failed to update finance details');
    }
  };

  const handleQuickAction = async (action) => {
    try {
      switch (action) {
        case 'edit':
          if (status === 'draft') setEditBookingDialogOpen(true);
          break;
        case 'edit_finance':
          if (status === 'draft') openEditFinance();
          else openErrorModal('Warning', 'Only draft bookings can be edited');
          break;
        case 'pending':
          if (status === 'draft') openEditFinance();
          else openErrorModal('Warning', 'Only draft bookings can be submitted for approval');
          break;
        case 'confirmed': {
          setNewStatus(action);
          openConfirmModal(
            'Review Finance Before Approval',
            <>
              Please ensure the finance details for this booking are filled and accurate before approving.
              <br /><br />
              Do you still want to proceed with approval?
            </>,
            () => { setEditNotesDialogOpen(true); },
            'Proceed to Approve'
          );
          break;
        }
        case 'completed':
        case 'cancelled':
          setNewStatus(action);
          setEditNotesDialogOpen(true);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error performing quick action:', error);
      openErrorModal('Error', 'Failed to perform action');
    }
  };

  // Primary customer editing handlers
  const handleEditPrimaryCustomer = () => {
    setPrimaryCustomerForm({
      customer_name: booking?.customer?.customer_name || '',
      customer_email: booking?.customer?.customer_email || '',
      customer_phone: booking?.customer?.customer_phone || ''
    });
    setEditingPrimaryCustomer(true);
  };

  const handlePrimaryCustomerFormChange = (e) => {
    setPrimaryCustomerForm({ ...primaryCustomerForm, [e.target.name]: e.target.value });
  };

  const handlePrimaryCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!primaryCustomerForm.customer_name.trim()) {
      openErrorModal('Error', 'Customer name is required');
      return;
    }
    setFormLoading(true);
    try {
      const customerData = {
        customer_name: primaryCustomerForm.customer_name.trim(),
        customer_email: primaryCustomerForm.customer_email.trim(),
        customer_phone: primaryCustomerForm.customer_phone.trim()
      };
      await updatePrimaryCustomer(booking.id, customerData);
      setEditingPrimaryCustomer(false);
      openSuccessModal('Success', 'Primary customer updated successfully!');
      if (onActionComplete) onActionComplete();
    } catch (err) {
      console.error('Error updating primary customer:', err);
      openErrorModal('Error', 'Failed to update primary customer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelPrimaryCustomer = () => {
    setEditingPrimaryCustomer(false);
    setPrimaryCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
  };

  // Booking date handlers
  const handleEditBookingDate = () => {
    setBookingDate(dayjs(booking_date));
    setEditingBookingDate(true);
  };

  const handleSaveBookingDate = async () => {
    if (!bookingDate) { openErrorModal('Error', 'Please select a booking date'); return; }
    setFormLoading(true);
    try {
      await updateBookingDates(booking.id, 'booking', bookingDate.format('YYYY-MM-DD'));
      setEditingBookingDate(false);
      openSuccessModal('Success', 'Booking date updated successfully!');
      if (onActionComplete) onActionComplete();
    } catch (err) {
      console.error('Error updating booking date:', err);
      openErrorModal('Error', 'Failed to update booking date');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelBookingDate = () => {
    setEditingBookingDate(false);
    // ✅ FIX: Reset to actual booking date, not null
    setBookingDate(booking_date ? dayjs(booking_date) : null);
  };

  // Token date handlers
  const handleEditTokenDate = () => {
    setTokenDate(booking?.sale_order?.token_paid_on ? dayjs(booking.sale_order.token_paid_on) : null);
    setEditingTokenDate(true);
  };

  const handleSaveTokenDate = async () => {
    if (tokenDate && booking_date) {
      const tokenDateObj = tokenDate.startOf('day').toDate();
      const bookingDateObj = dayjs(booking_date).startOf('day').toDate();
      if (tokenDateObj < bookingDateObj) {
        openErrorModal('Error', 'Token paid date cannot be earlier than booking date');
        return;
      }
    }
    setFormLoading(true);
    try {
      await updateBookingDates(booking.id, 'token', tokenDate ? tokenDate.format('YYYY-MM-DD') : null);
      setEditingTokenDate(false);
      openSuccessModal('Success', 'Token paid date updated successfully!');
      if (onActionComplete) onActionComplete();
    } catch (err) {
      console.error('Error updating token paid date:', err);
      openErrorModal('Error', 'Failed to update token paid date');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelTokenDate = () => {
    setEditingTokenDate(false);
    // ✅ FIX: Reset to actual token date, not null
    setTokenDate(booking?.sale_order?.token_paid_on ? dayjs(booking.sale_order.token_paid_on) : null);
  };

  const handleStatusMenuOpen = (event) => {
    if (event && event.currentTarget) {
      setStatusMenuAnchor(event.currentTarget);
    } else {
      setStatusMenuAnchor(statusTriggerRef.current);
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedNewStatus('');
  };

  const handleStatusChange = (newStatus) => {
    setSelectedNewStatus(newStatus);
    setNewStatus(newStatus);

    if (status === 'draft' && newStatus === 'pending') {
      openEditFinance();
      handleStatusMenuClose();
      return;
    }

    if (status !== 'draft' && newStatus === 'pending') {
      openErrorModal('Warning', 'Only draft bookings can be submitted for approval');
      handleStatusMenuClose();
      return;
    }

    if (newStatus === 'confirmed') {
      openConfirmModal(
        'Review Finance Before Approval',
        <>
          Please ensure the finance details for this booking are filled and accurate before approving.
          <br /><br />
          Do you still want to proceed with approval?
        </>,
        () => { setEditNotesDialogOpen(true); },
        'Proceed to Approve'
      );
      handleStatusMenuClose();
      return;
    }

    setEditNotesDialogOpen(true);
    handleStatusMenuClose();
  };

  const getAvailableStatusOptions = () => {
    const options = [];
    switch (status) {
      case 'draft':
        options.push({ value: 'pending', label: 'Submit for Review' });
        options.push({ value: 'cancelled', label: 'Cancel Booking' });
        break;
      case 'pending':
        options.push({ value: 'confirmed', label: 'Approve Booking' });
        options.push({ value: 'cancelled', label: 'Cancel Booking' });
        break;
      case 'reserved':
        options.push({ value: 'confirmed', label: 'Approve Booking' });
        options.push({ value: 'cancelled', label: 'Cancel Booking' });
        break;
      case 'confirmed':
        options.push({ value: 'completed', label: 'Mark as Completed' });
        options.push({ value: 'cancelled', label: 'Cancel Booking' });
        break;
      case 'completed':
      case 'cancelled':
      case 'expired':
        break;
      default:
        break;
    }
    return options;
  };

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const bookingFilters = await fetchBookingFilters();
        setReferenceData({
          agents: bookingFilters.agents || [],
          projects: bookingFilters.projects || [],
          leads: bookingFilters.leads || [],
          bookingStatusOptions: bookingFilters.booking_statuses || [],
          paymentStatusOptions: bookingFilters.payment_statuses || [],
          paymentModeOptions: bookingFilters.payment_modes || [],
          units: bookingFilters.units || {},
          filtered_projects: bookingFilters.filtered_projects || [],
          other_charges: bookingFilters.other_charges || []
        });
      } catch (error) {
        setReferenceData(defaultReferenceData);
      }
    };
    fetchReferenceData();
  }, []);

  const availableStatusOptions = getAvailableStatusOptions();

  if (!booking) return null;

  // ✅ FIX: Status menu with explicit per-option colors via inline sx on each element.
  // The old pattern of `color: \`${option.color}.main\`` on MenuItem does not propagate
  // correctly to child icons and text in MUI — each element now has its own explicit color.
  const statusMenu = (
    <Menu
      anchorEl={statusMenuAnchor}
      open={Boolean(statusMenuAnchor)}
      onClose={handleStatusMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { minWidth: 200, mt: 1, borderRadius: 1.5, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
    >
      {availableStatusOptions.map((option) => {
        const iconColor =
          option.value === 'cancelled' ? '#ef4444'
          : option.value === 'confirmed' || option.value === 'completed' ? '#22c55e'
          : option.value === 'pending' ? '#f59e0b'
          : 'inherit';

        const textColor =
          option.value === 'cancelled' ? '#dc2626'
          : option.value === 'confirmed' || option.value === 'completed' ? '#16a34a'
          : option.value === 'pending' ? '#d97706'
          : 'inherit';

        const hoverBg =
          option.value === 'cancelled' ? '#fef2f2'
          : option.value === 'confirmed' || option.value === 'completed' ? '#f0fdf4'
          : option.value === 'pending' ? '#fffbeb'
          : 'action.hover';

        return (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            sx={{ py: 1, px: 2, '&:hover': { bgcolor: hoverBg } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {option.value === 'confirmed' && <CheckCircleIcon fontSize="small" sx={{ color: iconColor }} />}
              {option.value === 'completed' && <CheckCircleIcon fontSize="small" sx={{ color: iconColor }} />}
              {option.value === 'cancelled' && <CancelIcon fontSize="small" sx={{ color: iconColor }} />}
              {option.value === 'pending' && <ScheduleIcon fontSize="small" sx={{ color: iconColor }} />}
              <Typography variant="body2" sx={{ fontWeight: 500, color: textColor }}>
                {option.label}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Menu>
  );

  const datesRow = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Booking Date:</Typography>
        {editingBookingDate ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker value={bookingDate} onChange={(v) => setBookingDate(v)} renderInput={(params) => <TextField {...params} size="small" sx={{ width: '140px', '& .MuiInputBase-root': { height: '36px' } }} />} format="DD/MM/YYYY" />
            </LocalizationProvider>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" variant="contained" onClick={handleSaveBookingDate} disabled={formLoading || !bookingDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Save</Button>
              <Button size="small" onClick={handleCancelBookingDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Cancel</Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: '1.5px solid', borderColor: (status === 'draft' || status === 'pending') ? bookingTokens.green : bookingTokens.border, bgcolor: (status === 'draft' || status === 'pending') ? bookingTokens.greenLt : 'transparent', cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default', height: '36px', minWidth: '120px' }} onClick={(status === 'draft' || status === 'pending') ? handleEditBookingDate : undefined}>
            <CalendarTodayIcon sx={{ color: (status === 'draft' || status === 'pending') ? bookingTokens.green : 'text.secondary', fontSize: 14 }} />
            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>{booking_date ? new Date(booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', flexShrink: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Token Paid On:</Typography>
        {editingTokenDate ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker value={tokenDate} onChange={(v) => setTokenDate(v)} renderInput={(params) => <TextField {...params} size="small" placeholder="Select date" sx={{ width: '140px', '& .MuiInputBase-root': { height: '36px' } }} />} format="DD/MM/YYYY" minDate={booking_date ? dayjs(booking_date) : undefined} />
            </LocalizationProvider>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" variant="contained" onClick={handleSaveTokenDate} disabled={formLoading} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Save</Button>
              <Button size="small" onClick={handleCancelTokenDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Cancel</Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: '1.5px solid', borderColor: booking?.sale_order?.token_paid_on ? bookingTokens.border : bookingTokens.amber, bgcolor: booking?.sale_order?.token_paid_on ? 'transparent' : bookingTokens.amberLt, cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default', height: '36px', minWidth: booking?.sale_order?.token_paid_on ? '120px' : '140px' }} onClick={(status === 'draft' || status === 'pending') ? handleEditTokenDate : undefined}>
            <PaymentIcon sx={{ color: booking?.sale_order?.token_paid_on ? 'text.secondary' : bookingTokens.amber, fontSize: 14 }} />
            {booking?.sale_order?.token_paid_on
              ? <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>{new Date(booking.sale_order.token_paid_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
              : <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem', color: bookingTokens.amber }}>Click to add</Typography>
            }
          </Box>
        )}
      </Box>
    </>
  );

  if (compactLayout) {
    const statusAccentColor = {
      draft: '#94a3b8',
      pending: '#f59e0b',
      reserved: '#3b82f6',
      confirmed: '#10b981',
      completed: '#6366f1',
      cancelled: '#ef4444',
      expired: '#9ca3af',
    }[status] || bookingTokens.border;

    const statusPalette = {
      draft:     { bg: '#f1f5f9', border: '#cbd5e1', dot: '#94a3b8', text: '#475569' },
      pending:   { bg: '#fffbeb', border: '#fcd34d', dot: '#f59e0b', text: '#92400e' },
      reserved:  { bg: '#eff6ff', border: '#93c5fd', dot: '#3b82f6', text: '#1e40af' },
      confirmed: { bg: '#f0fdf4', border: '#86efac', dot: '#22c55e', text: '#166534' },
      completed: { bg: '#f5f3ff', border: '#c4b5fd', dot: '#8b5cf6', text: '#4c1d95' },
      cancelled: { bg: '#fef2f2', border: '#fca5a5', dot: '#ef4444', text: '#991b1b' },
      expired:   { bg: '#f9fafb', border: '#d1d5db', dot: '#9ca3af', text: '#4b5563' },
    };
    const sp = statusPalette[status] || statusPalette.draft;

    return (
      <>
        <Box
          sx={{
            bgcolor: bookingTokens.white,
            borderBottom: `1.5px solid ${bookingTokens.border}`,
            borderLeft: `3.5px solid ${statusAccentColor}`,
            px: 2.5,
            py: '9px',
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flexWrap: 'nowrap',
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          {/* Booking ID pill */}
          <Tooltip open={copyTooltipOpen} title="Copied!" arrow placement="top">
            <Box
              onClick={handleCopy}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopy(); } }}
              aria-label="Copy booking ID"
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                cursor: 'pointer', flexShrink: 0, mr: 2,
                bgcolor: bookingTokens.bg,
                border: `1px solid ${bookingTokens.border}`,
                borderRadius: '6px',
                px: 1, py: '3px',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: bookingTokens.subtle, bgcolor: bookingTokens.borderL, '& .copy-icon': { opacity: 1 } },
              }}
            >
              <ContentCopyIcon className="copy-icon" sx={{ fontSize: 10.5, color: bookingTokens.muted, opacity: 0, transition: 'opacity 0.15s ease' }} />
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.9375rem', color: bookingTokens.ink, letterSpacing: '0.2px' }}>
                {booking_id}
              </Typography>
            </Box>
          </Tooltip>

          <Box sx={{ width: '1px', height: 16, bgcolor: bookingTokens.border, flexShrink: 0, mr: 2 }} />

          {/* Dates */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>

            {/* Booking date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: bookingTokens.muted, whiteSpace: 'nowrap' }}>Booking</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={bookingDate}
                  open={bookingDatePickerOpen}
                  onOpen={() => (status === 'draft' || status === 'pending') && setBookingDatePickerOpen(true)}
                  onClose={() => setBookingDatePickerOpen(false)}
                  onChange={async (v) => {
                    if (!v) return;
                    setBookingDate(v);
                    setBookingDatePickerOpen(false);
                    setFormLoading(true);
                    try {
                      await updateBookingDates(booking.id, 'booking', v.format('YYYY-MM-DD'));
                      openSuccessModal('Success', 'Booking date updated successfully!');
                      if (onActionComplete) onActionComplete();
                    } catch (err) {
                      openErrorModal('Error', 'Failed to update booking date');
                    } finally {
                      setFormLoading(false);
                    }
                  }}
                  format="DD/MM/YYYY"
                  slots={{ textField: 'div' }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      anchorEl: bookingDateTriggerRef.current,
                    },
                    textField: { size: 'small' },
                  }}
                >
                  {() => null}
                </DatePicker>
              </LocalizationProvider>
              <Box
                ref={bookingDateTriggerRef}
                onClick={() => { if (status === 'draft' || status === 'pending') setBookingDatePickerOpen(true); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.75, py: '2.5px', borderRadius: '5px', border: '1px solid',
                  borderColor: (status === 'draft' || status === 'pending') ? bookingTokens.green : bookingTokens.border,
                  bgcolor: (status === 'draft' || status === 'pending') ? bookingTokens.greenLt : bookingTokens.bg,
                  cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  '&:hover': (status === 'draft' || status === 'pending') ? { filter: 'brightness(0.97)' } : {},
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 10.5, color: (status === 'draft' || status === 'pending') ? bookingTokens.green : bookingTokens.muted }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: bookingTokens.ink2, whiteSpace: 'nowrap' }}>
                  {booking_date ? new Date(booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </Typography>
              </Box>
            </Box>

            {/* Token date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: bookingTokens.muted, whiteSpace: 'nowrap' }}>Token</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={tokenDate}
                  open={tokenDatePickerOpen}
                  onOpen={() => (status === 'draft' || status === 'pending') && setTokenDatePickerOpen(true)}
                  onClose={() => setTokenDatePickerOpen(false)}
                  minDate={booking_date ? dayjs(booking_date) : undefined}
                  onChange={async (v) => {
                    if (!v) return;
                    if (booking_date) {
                      const tokenD = v.startOf('day').toDate();
                      const bookingD = dayjs(booking_date).startOf('day').toDate();
                      if (tokenD < bookingD) {
                        openErrorModal('Error', 'Token paid date cannot be earlier than booking date');
                        setTokenDatePickerOpen(false);
                        return;
                      }
                    }
                    setTokenDate(v);
                    setTokenDatePickerOpen(false);
                    setFormLoading(true);
                    try {
                      await updateBookingDates(booking.id, 'token', v.format('YYYY-MM-DD'));
                      openSuccessModal('Success', 'Token paid date updated successfully!');
                      if (onActionComplete) onActionComplete();
                    } catch (err) {
                      openErrorModal('Error', 'Failed to update token paid date');
                    } finally {
                      setFormLoading(false);
                    }
                  }}
                  format="DD/MM/YYYY"
                  slots={{ textField: 'div' }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      anchorEl: tokenDateTriggerRef.current,
                    },
                    textField: { size: 'small' },
                  }}
                >
                  {() => null}
                </DatePicker>
              </LocalizationProvider>
              <Box
                ref={tokenDateTriggerRef}
                onClick={() => { if (status === 'draft' || status === 'pending') setTokenDatePickerOpen(true); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  px: 0.75, py: '2.5px', borderRadius: '5px', border: '1px solid',
                  borderColor: booking?.sale_order?.token_paid_on ? bookingTokens.border : bookingTokens.amber,
                  bgcolor: booking?.sale_order?.token_paid_on ? bookingTokens.bg : bookingTokens.amberLt,
                  cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  '&:hover': (status === 'draft' || status === 'pending') ? { filter: 'brightness(0.97)' } : {},
                }}
              >
                <PaymentIcon sx={{ fontSize: 10.5, color: booking?.sale_order?.token_paid_on ? bookingTokens.muted : bookingTokens.amber }} />
                {booking?.sale_order?.token_paid_on
                  ? <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: bookingTokens.ink2, whiteSpace: 'nowrap' }}>{new Date(booking.sale_order.token_paid_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
                  : <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: bookingTokens.amber, whiteSpace: 'nowrap' }}>+ Add</Typography>
                }
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: '1px', height: 16, bgcolor: bookingTokens.border, flexShrink: 0, mx: 2 }} />

          {/* Custom status badge */}
          <Box
            ref={statusTriggerRef}
            onClick={() => { if (availableStatusOptions.length > 0) handleStatusMenuOpen(); }}
            role={availableStatusOptions.length > 0 ? 'button' : undefined}
            tabIndex={availableStatusOptions.length > 0 ? 0 : -1}
            onKeyDown={(e) => { if (availableStatusOptions.length > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleStatusMenuOpen(); } }}
            aria-label={availableStatusOptions.length > 0 ? 'Change booking status' : 'Booking status'}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.625,
              flexShrink: 0,
              px: 1, py: '4px',
              borderRadius: '6px',
              border: `1px solid ${sp.border}`,
              bgcolor: sp.bg,
              cursor: availableStatusOptions.length > 0 ? 'pointer' : 'default',
              transition: 'all 0.15s ease',
              userSelect: 'none',
              '&:hover': availableStatusOptions.length > 0 ? {
                filter: 'brightness(0.97)',
                boxShadow: `0 0 0 2px ${sp.border}`,
                '& .chevron-icon': { opacity: 1, transform: 'translateY(1px)' },
              } : {},
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {availableStatusOptions.length > 0 && (
                <Box sx={{
                  position: 'absolute',
                  width: 14, height: 14,
                  borderRadius: '50%',
                  bgcolor: sp.dot,
                  opacity: 0.25,
                  animation: 'statusPulse 2s ease-in-out infinite',
                  '@keyframes statusPulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 0.25 },
                    '50%': { transform: 'scale(1.7)', opacity: 0 },
                  },
                }} />
              )}
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: sp.dot, flexShrink: 0, position: 'relative', zIndex: 1 }} />
            </Box>
            <Typography sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: sp.text,
              letterSpacing: '0.1px',
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
            }}>
              {booking.status_verbose || booking.status}
            </Typography>
            {availableStatusOptions.length > 0 && (
              <Box
                className="chevron-icon"
                component="svg"
                viewBox="0 0 10 6"
                sx={{
                  width: 9, height: 9, flexShrink: 0,
                  opacity: 0.5,
                  transition: 'all 0.15s ease',
                  fill: 'none',
                  stroke: sp.text,
                  strokeWidth: 1.75,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
              >
                <polyline points="1 1 5 5 9 1" />
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ bgcolor: bookingTokens.white, p: '10px 24px', borderBottom: `1.5px solid ${bookingTokens.border}`, flexShrink: 0 }}>
          <Typography sx={{ fontSize: '9.5px', fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.875 }}>Primary Customer</Typography>
          {editingPrimaryCustomer ? (
            <Box component="form" onSubmit={handlePrimaryCustomerSubmit}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}><TextField label="Name" name="customer_name" value={primaryCustomerForm.customer_name} onChange={handlePrimaryCustomerFormChange} fullWidth required size="small" error={!primaryCustomerForm.customer_name.trim()} sx={{ '& .MuiInputBase-root': { height: '40px' } }} /></Grid>
                <Grid item xs={12} md={4}><TextField label="Email" name="customer_email" value={primaryCustomerForm.customer_email} onChange={handlePrimaryCustomerFormChange} fullWidth size="small" placeholder="Optional" sx={{ '& .MuiInputBase-root': { height: '40px' } }} /></Grid>
                <Grid item xs={12} md={4}><PhoneInput size="small" label="Phone" value={primaryCustomerForm.customer_phone} onChange={(val) => setPrimaryCustomerForm((p) => ({ ...p, customer_phone: val }))} required={false} /></Grid>
                <Grid item xs={12}><Stack direction="row" spacing={1}><Button type="submit" variant="contained" size="small" disabled={formLoading || !primaryCustomerForm.customer_name.trim()} sx={{ height: '36px' }}>Save</Button><Button onClick={handleCancelPrimaryCustomer} size="small" sx={{ height: '36px' }}>Cancel</Button></Stack></Grid>
              </Grid>
            </Box>
          ) : !booking?.lead?.id && (status === 'draft' || status === 'pending') ? (
            <Box
              component="button" type="button" onClick={() => setEditBookingDialogOpen(true)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%', py: 2.5, px: 2, bgcolor: bookingTokens.bg, border: `1.5px dashed ${bookingTokens.border}`, borderRadius: 1.25, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', '&:hover': { borderColor: bookingTokens.green, bgcolor: bookingTokens.greenSf } }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: bookingTokens.green }}>Add Customer</Typography>
              <Typography sx={{ fontSize: 12, color: bookingTokens.muted }}>Select Lead</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: bookingTokens.greenLt, border: `1px solid ${bookingTokens.border}`, borderRadius: 1.25, p: '9px 15px', flexWrap: 'nowrap', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flexShrink: 0, minWidth: 0 }}><PersonIcon sx={{ fontSize: 14, color: bookingTokens.green }} /><Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.green }}>{booking?.customer?.customer_name || 'Not provided'}</Typography></Box>
              <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}><EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} /><Typography noWrap sx={{ fontSize: 12.5, color: booking?.customer?.customer_email ? bookingTokens.ink2 : bookingTokens.muted, fontStyle: booking?.customer?.customer_email ? 'normal' : 'italic' }}>{booking?.customer?.customer_email || 'Not provided'}</Typography></Box>
              <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}><PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} /><Typography noWrap sx={{ fontSize: 12.5, color: bookingTokens.ink2, fontWeight: 600 }}>{booking?.customer?.customer_phone || 'Not provided'}</Typography></Box>
              {(status === 'draft' || status === 'pending') && (<><Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0, ml: 1 }} /><IconButton size="small" onClick={handleEditPrimaryCustomer} aria-label="Edit primary customer" sx={{ width: 32, height: 32, flexShrink: 0 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton></>)}
            </Box>
          )}
        </Box>

        {statusMenu}
        <AddBookingFinance open={editFinanceDialogOpen} onClose={closeEditFinance} booking={booking} onSave={handleSaveFinance} isEdit={true} otherCharges={referenceData.other_charges || []} isStatusUpdate={new_status === 'pending'} statusAction={new_status} />
        <CreateNewBooking open={editBookingDialogOpen} onClose={handleEditBookingClose} initialData={booking} leads={referenceData.leads || []} units={referenceData.units || {}} filtered_projects={referenceData.filtered_projects || []} />
        <BookingNotes open={editNotesDialogOpen} onClose={() => { setEditNotesDialogOpen(false); setNewStatus(null); }} onSave={() => onActionComplete && onActionComplete()} bookingId={booking.id} action={new_status} booking={booking} />
      </>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', transition: 'all 0.2s ease', '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.06)' } }}
      >
        {/* Header: Booking ID | Status | Finance */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'nowrap', minWidth: 0 }}>

          {/* Booking ID */}
          <Tooltip open={copyTooltipOpen} title="Copied to clipboard!" arrow placement="top">
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', flex: 1, minWidth: 0, '&:hover .copy-icon': { opacity: 1 } }}
              onClick={handleCopy} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopy(); } }}
              aria-label="Copy booking ID"
            >
              <ContentCopyIcon className="copy-icon" sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5, transition: 'opacity 0.2s ease' }} />
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem', color: 'text.primary', letterSpacing: '0.3px' }}>
                {booking_id}
              </Typography>
            </Box>
          </Tooltip>

          <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', display: { xs: 'none', sm: 'block' } }} />

          <Box
            ref={statusTriggerRef}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              cursor: availableStatusOptions.length > 0 ? 'pointer' : 'default',
              '&:hover': availableStatusOptions.length > 0 ? { '& .status-edit-icon': { opacity: 1 } } : {}
            }}
            onClick={() => { if (availableStatusOptions.length > 0) handleStatusMenuOpen(); }}
            role={availableStatusOptions.length > 0 ? 'button' : undefined}
            tabIndex={availableStatusOptions.length > 0 ? 0 : -1}
            onKeyDown={(e) => { if (availableStatusOptions.length > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleStatusMenuOpen(); } }}
            aria-label={availableStatusOptions.length > 0 ? 'Change booking status' : 'Booking status (read-only)'}
          >
            <Chip
              label={booking.status_verbose || booking.status}
              color={getBookingStatusColor(booking.status)}
              size="small"
              sx={{
                textTransform: 'capitalize', fontWeight: 600, fontSize: '0.75rem', height: '24px', px: 1.25,
                cursor: availableStatusOptions.length > 0 ? 'pointer' : 'default',
                border: availableStatusOptions.length > 0 ? '1px solid' : 'none',
                borderColor: availableStatusOptions.length > 0 ? 'divider' : 'transparent',
                '&:hover': availableStatusOptions.length > 0 ? { borderColor: 'primary.main', bgcolor: 'action.hover', transform: 'translateY(-1px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' } : {},
                transition: 'all 0.2s ease'
              }}
            />
            {availableStatusOptions.length > 0 && (
              <EditIcon className="status-edit-icon" sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.4, transition: 'opacity 0.2s ease', cursor: 'pointer' }} />
            )}
          </Box>

          {/* Finance Button */}
          {(status === 'draft' || status === 'pending') && (
            <>
              <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', display: { xs: 'none', sm: 'block' } }} />
              <Button
                variant="contained" size="small"
                startIcon={<CurrencyRupeeIcon sx={{ fontSize: 14 }} />}
                onClick={() => openEditFinance()}
                sx={{ height: '36px', borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', px: 1.5, py: 0, bgcolor: 'success.main', boxShadow: 'none', '&:hover': { bgcolor: 'success.dark', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' } }}
              >
                {status === 'draft' ? 'Add Finance' : 'Update Finance'}
              </Button>
            </>
          )}
        </Box>

        {/* Dates Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'nowrap', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Booking Date:</Typography>
            {editingBookingDate ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={bookingDate}
                    onChange={(newValue) => setBookingDate(newValue)}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ width: '140px', '& .MuiInputBase-root': { height: '36px' } }} />}
                    format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="contained" onClick={handleSaveBookingDate} disabled={formLoading || !bookingDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Save</Button>
                  <Button size="small" onClick={handleCancelBookingDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Cancel</Button>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: '1.5px solid', borderColor: (status === 'draft' || status === 'pending') ? bookingTokens.green : bookingTokens.border, bgcolor: (status === 'draft' || status === 'pending') ? bookingTokens.greenLt : 'transparent', cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default', transition: 'all 0.2s ease', height: '36px', minWidth: '120px', '&:hover': (status === 'draft' || status === 'pending') ? { borderColor: bookingTokens.green, bgcolor: bookingTokens.greenLt, '& .edit-indicator': { opacity: 1 } } : {} }}
                onClick={(status === 'draft' || status === 'pending') ? handleEditBookingDate : undefined}
                onKeyDown={(e) => { if ((status === 'draft' || status === 'pending') && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleEditBookingDate(); } }}
                tabIndex={(status === 'draft' || status === 'pending') ? 0 : -1}
                role={(status === 'draft' || status === 'pending') ? 'button' : undefined}
                aria-label={(status === 'draft' || status === 'pending') ? 'Edit booking date' : 'Booking date (read-only)'}
              >
                <CalendarTodayIcon sx={{ color: (status === 'draft' || status === 'pending') ? bookingTokens.green : 'text.secondary', fontSize: 14, opacity: (status === 'draft' || status === 'pending') ? 1 : 0.6 }} />
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                  {new Date(booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Typography>
                {(status === 'draft' || status === 'pending') && (
                  <EditIcon className="edit-indicator" sx={{ fontSize: 12, color: bookingTokens.green, opacity: 0, transition: 'opacity 0.2s ease', ml: 'auto' }} />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', flexShrink: 0 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Token Paid On:</Typography>
            {editingTokenDate ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={tokenDate}
                    onChange={(newValue) => setTokenDate(newValue)}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Select date" sx={{ width: '140px', '& .MuiInputBase-root': { height: '36px' } }} helperText={booking_date ? `Token date must be on or after ${dayjs(booking_date).format('DD/MM/YYYY')}` : ''} />}
                    format="DD/MM/YYYY"
                    minDate={booking_date ? dayjs(booking_date) : undefined}
                  />
                </LocalizationProvider>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="contained" onClick={handleSaveTokenDate} disabled={formLoading} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Save</Button>
                  <Button size="small" onClick={handleCancelTokenDate} sx={{ height: '32px', minWidth: '60px', fontSize: '0.75rem' }}>Cancel</Button>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: '1.5px solid', borderColor: booking?.sale_order?.token_paid_on ? bookingTokens.border : bookingTokens.amber, bgcolor: booking?.sale_order?.token_paid_on ? 'transparent' : bookingTokens.amberLt, cursor: (status === 'draft' || status === 'pending') ? 'pointer' : 'default', height: '36px', minWidth: booking?.sale_order?.token_paid_on ? '120px' : '140px', '&:hover': (status === 'draft' || status === 'pending') ? { borderColor: bookingTokens.amber, bgcolor: bookingTokens.amberLt, '& .edit-indicator': { opacity: 1 } } : {} }}
                onClick={(status === 'draft' || status === 'pending') ? handleEditTokenDate : undefined}
                onKeyDown={(e) => { if ((status === 'draft' || status === 'pending') && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleEditTokenDate(); } }}
                tabIndex={(status === 'draft' || status === 'pending') ? 0 : -1}
                role={(status === 'draft' || status === 'pending') ? 'button' : undefined}
                aria-label={(status === 'draft' || status === 'pending') ? 'Edit token paid date' : 'Token paid date (read-only)'}
              >
                <PaymentIcon sx={{ color: booking?.sale_order?.token_paid_on ? 'text.secondary' : bookingTokens.amber, fontSize: 14, opacity: booking?.sale_order?.token_paid_on ? 0.6 : 1 }} />
                {booking?.sale_order?.token_paid_on
                  ? <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>{new Date(booking.sale_order.token_paid_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
                  : <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem', color: bookingTokens.amber }}>Click to add</Typography>
                }
                {(status === 'draft' || status === 'pending') && (
                  <EditIcon className="edit-indicator" sx={{ fontSize: 12, color: booking?.sale_order?.token_paid_on ? 'text.secondary' : bookingTokens.amber, opacity: booking?.sale_order?.token_paid_on ? 0 : 0.6, transition: 'opacity 0.2s ease', ml: 'auto' }} />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Customer Section */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '9.5px', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.6px', color: bookingTokens.muted, display: 'block' }}>
            Primary Customer
          </Typography>

          {editingPrimaryCustomer ? (
            <Box component="form" onSubmit={handlePrimaryCustomerSubmit}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}><TextField label="Name" name="customer_name" value={primaryCustomerForm.customer_name} onChange={handlePrimaryCustomerFormChange} fullWidth required size="small" error={!primaryCustomerForm.customer_name.trim()} helperText={!primaryCustomerForm.customer_name.trim() ? 'Name is required' : ''} sx={{ '& .MuiInputBase-root': { height: '40px' } }} /></Grid>
                <Grid item xs={12} md={4}><TextField label="Email" name="customer_email" value={primaryCustomerForm.customer_email} onChange={handlePrimaryCustomerFormChange} fullWidth size="small" placeholder="Optional" sx={{ '& .MuiInputBase-root': { height: '40px' } }} /></Grid>
                <Grid item xs={12} md={4}><PhoneInput size="small" label="Phone" value={primaryCustomerForm.customer_phone} onChange={(val) => setPrimaryCustomerForm((prev) => ({ ...prev, customer_phone: val }))} required={false} /></Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button type="submit" variant="contained" size="small" disabled={formLoading || !primaryCustomerForm.customer_name.trim()} sx={{ height: '36px', fontSize: '0.8125rem' }}>Save</Button>
                    <Button onClick={handleCancelPrimaryCustomer} size="small" sx={{ height: '36px', fontSize: '0.8125rem' }}>Cancel</Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          ) : !booking?.lead?.id && (status === 'draft' || status === 'pending') ? (
            <Box
              component="button" type="button" onClick={() => setEditBookingDialogOpen(true)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%', py: 2.5, px: 2, bgcolor: bookingTokens.bg, border: `1.5px dashed ${bookingTokens.border}`, borderRadius: 1.25, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', '&:hover': { borderColor: bookingTokens.green, bgcolor: bookingTokens.greenSf } }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: bookingTokens.green }}>Add Customer</Typography>
              <Typography sx={{ fontSize: 12, color: bookingTokens.muted }}>Select Lead</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '9px 15px', bgcolor: bookingTokens.greenLt, borderRadius: 1.25, border: `1px solid ${bookingTokens.border}`, position: 'relative', flexWrap: 'nowrap', minWidth: 0, '&:hover': { borderColor: bookingTokens.subtle, '& .edit-button': { opacity: 1 } }, transition: 'all 0.2s ease' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flexShrink: 0, minWidth: 0 }}>
                <PersonIcon sx={{ fontSize: 14, color: bookingTokens.green }} />
                {booking?.lead?.id ? (
                  <Typography component={RouterLink} to={`/leads/${booking.lead.id}`} noWrap sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.green, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {booking?.customer?.customer_name || 'Not provided'}
                  </Typography>
                ) : (
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.green }}>{booking?.customer?.customer_name || 'Not provided'}</Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography noWrap sx={{ fontSize: 12.5, color: booking?.customer?.customer_email ? bookingTokens.ink2 : bookingTokens.muted, fontStyle: booking?.customer?.customer_email ? 'normal' : 'italic' }}>{booking?.customer?.customer_email || 'Not provided'}</Typography>
              </Box>
              <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography noWrap sx={{ fontSize: 12.5, color: bookingTokens.ink2, fontWeight: 600 }}>{booking?.customer?.customer_phone || 'Not provided'}</Typography>
              </Box>
              {(status === 'draft' || status === 'pending') && (
                <>
                  <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, bgcolor: bookingTokens.border, flexShrink: 0, ml: 'auto' }} />
                  <IconButton className="edit-button" size="small" onClick={handleEditPrimaryCustomer} sx={{ opacity: 0, transition: 'opacity 0.2s ease', width: 32, height: 32, flexShrink: 0, '&:hover': { bgcolor: bookingTokens.borderL } }} aria-label="Edit primary customer">
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {statusMenu}

      <AddBookingFinance
        open={editFinanceDialogOpen}
        onClose={() => closeEditFinance()}
        booking={booking}
        onSave={handleSaveFinance}
        isEdit={true}
        otherCharges={referenceData.other_charges || []}
        isStatusUpdate={new_status === 'pending'}
        statusAction={new_status}
      />

      <CreateNewBooking
        open={editBookingDialogOpen}
        onClose={handleEditBookingClose}
        initialData={booking}
        leads={referenceData.leads || []}
        units={referenceData.units || {}}
        filtered_projects={referenceData.filtered_projects || []}
      />

      <BookingNotes
        open={editNotesDialogOpen}
        onClose={() => { setEditNotesDialogOpen(false); setNewStatus(null); }}
        onSave={() => { if (onActionComplete && typeof onActionComplete === 'function') onActionComplete(); }}
        bookingId={booking.id}
        action={new_status}
        booking={booking}
      />
    </>
  );
}
